import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Event, ActivatedRouteSnapshot } from '@angular/router';
import { AuditService } from './audit.service';

@Injectable({ providedIn: 'root' })
export class RouteAuditTrackerService {
  private lastUrl = '';
  private lastPath = '';
  private lastQuery = '';
  private lastFrag = '';
  private navStartUrl = '';
  private initialized = false;

  constructor(private router: Router, private audit: AuditService, private zone: NgZone) {
    this.init();
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Captura navegação inicial se já houver URL
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        const currentUrl = this.router.url;
        if (currentUrl && currentUrl !== '/') {
          this.capturePageView(currentUrl, false);
        }
      }, 100);
    });

    // Subscribe aos eventos de navegação
    this.zone.runOutsideAngular(() => {
      this.router.events.subscribe((e) => this.onRouterEvent(e));
    });
  }

  private onRouterEvent(e: Event): void {
    if (e instanceof NavigationStart) {
      this.navStartUrl = e.url;
      return;
    }

    if (e instanceof NavigationEnd) {
      const url = e.urlAfterRedirects || e.url;
      const isRedirect = url !== this.navStartUrl;
      this.capturePageView(url, isRedirect);
      return;
    }

    if (e instanceof NavigationCancel || e instanceof NavigationError) {
      this.navStartUrl = '';
    }
  }

  private capturePageView(url: string, isRedirect: boolean): void {
    const { path, query, frag, change } = this.diffUrl(url);
    const canonicalId = this.canonicalFromSnapshot(this.router.routerState.snapshot.root) || path;

    // Sempre captura se for mudança de path ou primeira navegação
    if (change === 'path' || !this.lastUrl) {
      // Executa fora do Angular zone para não bloquear renderização
      this.zone.runOutsideAngular(() => {
        this.audit.submitAudit({
          tipoevento: 'page_view',
          recurso: { tipo: 'page', id: canonicalId },
          origem: isRedirect ? 'redirect' : (this.lastUrl ? 'spa_nav' : 'initial_load'),
          caminho: path,
          metodo: 'GET',
          codigostatus: 200
        }).subscribe({
          next: (response) => console.log('✅ [AUDIT] page_view sent:', response),
          error: (error) => console.error('❌ [AUDIT] page_view error:', error)
        });
      });
    }

    // Atualiza estado anterior
    this.lastUrl = url;
    this.lastPath = path;
    this.lastQuery = query;
    this.lastFrag = frag;
  }

  /** Constrói uma URL canônica só com o path, removendo query e fragmento */
  private canonicalFromRaw(url: string): string {
    const base = url.split('#')[0];
    const result = base.split('?')[0] || '/';
    console.log('🔧 [AUDIT] canonicalFromRaw:', { url, result });
    return result;
  }

  /**
   * Caminho canônico baseado nos patterns das rotas (usa :id ao invés de valores).
   * Ex.: '/clientes/42/detalhe' -> '/clientes/:id/detalhe'
   */
  private canonicalFromSnapshot(root: ActivatedRouteSnapshot | null): string | null {
    if (!root) {
      console.log('🔧 [AUDIT] canonicalFromSnapshot: no root');
      return null;
    }
    const parts: string[] = [];

    let node: ActivatedRouteSnapshot | null = root;
    while (node) {
      const p = node.routeConfig?.path;
      if (p) parts.push(p);
      node = (node.firstChild as ActivatedRouteSnapshot) || null;
    }

    const joined = '/' + parts.filter(Boolean).join('/');
    const result = joined === '/' ? null : joined;
    console.log('🔧 [AUDIT] canonicalFromSnapshot:', { parts, joined, result });
    return result;
  }

  private diffUrl(url: string): { path: string; query: string; frag: string; change: 'path'|'query'|'fragment'|'none' } {
    const [pAndQ, frag = ''] = url.split('#');
    const [path, query = ''] = pAndQ.split('?');

    let change: 'path'|'query'|'fragment'|'none' = 'none';
    
    // Primeira navegação sempre é mudança de path
    if (!this.lastUrl) {
      change = 'path';
    } else if (this.lastPath && path !== this.lastPath) {
      change = 'path';
    } else if (this.lastQuery && query !== this.lastQuery) {
      change = 'query';
    } else if (this.lastFrag && frag !== this.lastFrag) {
      change = 'fragment';
    }
    
    return { path, query, frag, change };
  }
}
